import { ReactElement, useState, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from '@tanstack/react-form'
import { Container, Typography, Card, TextField } from '@mui/material'
import { GridRowSelectionModel } from '@mui/x-data-grid'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import {
  fetchCourses,
  fetchLinkedServices,
  fetchTrainingSeries,
  selectCourses,
  selectCoursesStatus,
  selectLinkedServicesStatus,
} from '../manage.slice'
import CoursesDataGrid from './components/courses-data-grid'
import { TrainingSeriesFormData, trainingSeriesSchema } from './validation'
import { paperInputSx } from '../styles'
import {
  transformCoursesToRows,
  extractCategories,
  filterCourses,
} from './utils/course-transforms'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import CreateFooterActionButtons from '../components/cancel-save-buttons'
import { createTrainingSeries } from '../utils/api'
import { useNotifications } from '../hooks/use-notifications'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import { TRAINING_SERIES_URLS } from '../constants'

export default function TrainingSeriesCreateV4(): ReactElement {
  let navigate = useNavigate()
  let location = useLocation()
  let dispatch = useAppDispatch()
  let { showError, NotificationComponent } = useNotifications()

  let linkedServiceID = (location.state as { linkedServiceID?: string })
    ?.linkedServiceID
  let linkedServiceName = useLinkedServiceName(linkedServiceID)

  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let isLoading =
    coursesStatus === 'pending' || linkedServicesStatus === 'pending'

  let [selectedCourses, setSelectedCourses] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  })
  let [categoryFilter, setCategoryFilter] = useState('')
  let [searchQuery, setSearchQuery] = useState('')

  let courseRows = useMemo(() => transformCoursesToRows(courses), [courses])

  // Get distinct categories
  let availableCategories = useMemo(
    () => extractCategories(courseRows),
    [courseRows],
  )

  // Filter courses (always include selected courses)
  let filteredCourses = useMemo(
    () =>
      filterCourses(
        courseRows,
        Array.from(selectedCourses.ids) as string[],
        categoryFilter,
        searchQuery,
      ),
    [courseRows, categoryFilter, searchQuery, selectedCourses],
  )

  let form = useForm({
    defaultValues: {
      name: '',
      courseIDs: [],
    } as TrainingSeriesFormData,
    validators: {
      onMount: trainingSeriesSchema,
      onChange: trainingSeriesSchema,
    },
    onSubmit: async ({ value }) => {
      if (!linkedServiceID) return

      let payload = {
        name: value.name,
        linkedServiceID,
        courseIDs: value.courseIDs,
      }

      await createTrainingSeries(payload)
        .then(() => dispatch(fetchTrainingSeries()))
        .then(() => {
          navigate(TRAINING_SERIES_URLS.INDEX, {
            state: {
              successMessage: `Training series "${value.name}" created successfully.`,
              linkedServiceID,
            },
          })
        })
        .catch((err: any) => {
          let errorMessage = `Error creating training series "${value.name}".`
          // eslint-disable-next-line no-console
          console.error(errorMessage, 'Payload: ', payload, 'Error: ', err)
          showError(errorMessage)
        })
    },
  })

  let handleSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      setSelectedCourses(newSelection)

      // Calculate selected IDs based on selection model type
      let selectedIDs =
        newSelection.type === 'exclude'
          ? (() => {
              // Select all: get all visible IDs except excluded ones
              let excludedIDs = newSelection.ids
                ? (Array.from(newSelection.ids) as string[])
                : []
              return filteredCourses
                .map((row) => row.id)
                .filter((id) => !excludedIDs.includes(id))
            })()
          : newSelection.ids
            ? (Array.from(newSelection.ids) as string[])
            : []

      form.setFieldValue('courseIDs', selectedIDs)
      form.validate('change')
    },
    [filteredCourses, form],
  )

  let handleResetFilters = useCallback(() => {
    setSearchQuery('')
    setCategoryFilter('')
  }, [])

  let handleCancel = useCallback(() => {
    navigate(TRAINING_SERIES_URLS.INDEX, {
      state: { linkedServiceID },
    })
  }, [linkedServiceID, navigate])

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)
  useAutoFetch(coursesStatus, fetchCourses)

  return (
    <Container maxWidth="xl" className="py-6">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        aria-label="Create training series form"
      >
        <PageTitleWithLinkedService
          pageTitle="Create Training Series"
          moduleName="Training Series"
          linkedServiceName={linkedServiceName || ''}
          handleBackClick={handleCancel}
          className="mb-6"
          isLoading={isLoading}
        />

        <Card className="p-6 mb-6">
          <Typography variant="h6" className="mb-4">
            Training Series Name
          </Typography>
          <form.Field name="name">
            {(field) => (
              <TextField
                fullWidth
                required
                label="Name"
                placeholder="Enter training series name..."
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={shouldShowZodFieldError(field.state.meta)}
                helperText={
                  shouldShowZodFieldError(field.state.meta)
                    ? getZodValidationError(field.state.meta.errors)
                    : undefined
                }
                aria-label="Training series name"
                aria-invalid={shouldShowZodFieldError(field.state.meta)}
                sx={paperInputSx}
              />
            )}
          </form.Field>
        </Card>

        <form.Field name="courseIDs">
          {(field) => (
            <CoursesDataGrid
              courseRows={filteredCourses}
              selectedCourses={selectedCourses}
              onSelectionChange={handleSelectionChange}
              searchQuery={searchQuery}
              categoryFilter={categoryFilter}
              availableCategories={availableCategories}
              onSearchChange={setSearchQuery}
              onCategoryChange={setCategoryFilter}
              onResetFilters={handleResetFilters}
              isLoading={isLoading}
              field={field}
              editing={true}
              title="Select Courses"
            />
          )}
        </form.Field>
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
        >
          {([canSubmit, isSubmitting]) => (
            <CreateFooterActionButtons
              moduleName="Training Series"
              canSubmit={canSubmit}
              isSubmitting={isSubmitting}
              isDisabled={isLoading || isSubmitting}
              handleSave={form.handleSubmit}
              handleCancel={handleCancel}
            />
          )}
        </form.Subscribe>
      </form>
      {NotificationComponent}
    </Container>
  )
}
